<template>
  <div class="selectable-country-list"
       :element-loading-background="isDarkTheme ? '' : 'rgba(255, 255, 255, 1)'"
       v-loading="loading">
    <div class="search-container">
      <zm-autocomplete
        v-model="regionName"
        :fetch-suggestions="filterCountryList"
        :trigger-on-focus="false"
        aria-autocomplete="false"
        aria-placeholder="Search by Countries/Regions"
        clearable
        :placeholder="this.$t('billing.search_countries')"
        value-key="regionName"
        @select="handleRegionSelect"
        label="Search by Countries/Regions"
      >
        <i slot="prefix" class="zm-input__icon zm-icon-search"></i>
      </zm-autocomplete>
    </div>
    <div class="countries-container">
      <selection-table
        :data="viewCountryList"
        :height="383"
        :use-flip="false"
        ref="countriesTable"
        @select="handleSelect"
        @select-all="handleSelectAll"
        :init-selections="initSelections"
        row-key="id"
      >
        <zm-table-column
          type="selection"
          :reserve-selection="true"
          select-key="regionName"
          :selectable="getSelectable"
        >
        </zm-table-column>
        <template v-if="$context.is.desktop">
        <zm-table-column
          prop="regionName"
          :label="$t('billing.countries_regions')"
        >
        </zm-table-column>
        <zm-table-column
          :width="130"
          :label="$t('billing.tel.landline')"
        >
          <template slot-scope="scope">
            <span tabindex="0" class="column-span">{{ $money(scope.row.landLineRate, { fixedLength: 4, needRemoveDecimal: false, forceDiff: true }) }}</span>
          </template>
        </zm-table-column>
        <zm-table-column
          :width="130"
          :label="$t('billing.tel.mobile')"
        >
          <template slot-scope="scope">
            <span tabindex="0" class="column-span">{{ $money(scope.row.mobileRate, { fixedLength: 4, needRemoveDecimal: false, forceDiff: true }) }}</span>
          </template>
        </zm-table-column>
        </template>
        <zm-table-column
          v-else
          label="Select all"
        >
          <template slot-scope="scope">
              <zm-row :gutter="4">
                <zm-col :span="8">Name</zm-col>
                <zm-col :span="16">
                  {{ scope.row.regionName }}
                </zm-col>
              </zm-row>
              <zm-row :gutter="4">
                <zm-col :span="8">{{ $t('billing.tel.landline') }}</zm-col>
                <zm-col :span="16">
                  {{ $money(scope.row.mobileRate, { fixedLength: 4, needRemoveDecimal: false, forceDiff: true }) }}
                </zm-col>
              </zm-row>
              <zm-row :gutter="4">
                <zm-col :span="8">{{ $t('billing.tel.mobile') }}</zm-col>
                <zm-col :span="16">
                  {{ $money(scope.row.mobileRate, { fixedLength: 4, needRemoveDecimal: false, forceDiff: true }) }}
                </zm-col>
              </zm-row>
          </template>
        </zm-table-column>
      </selection-table>
    </div>
  </div>
</template>
<script>
import tabLoadingMixin from "@/common/mixin/tabLoadingMixin";
import SelectionTable from '@/components/SelectionTable';
import { mapGetters } from 'vuex';
import { THEME } from '@/views/checkout/constant.js';

export default {
  name: 'SelectableCountryTable',
  mixins: [tabLoadingMixin],
  computed: {
    ...mapGetters(['clientWebViewTheme']),
    isDarkTheme() {
      return this.clientWebViewTheme === THEME.DARK
    },
    viewCountryList() {
      return this.filterRegions.length && this.regionName ? this.filterRegions : this.countryList;
    },
    initSelections() {
      return this.countryList.filter(item => this.selectedCountries.indexOf(item.id) !== -1);
    },
  },
  data() {
    return {
      regionName: '',
      filterRegions: [],
    };
  },
  props: {
    countryList: Array,
    selectedCountries: Array,
  },
  methods: {
    filterCountryList(qs, cb) {
      const countryList = this.countryList;
      const results = qs ? countryList.filter(this.createFilter(qs)) : countryList;
      cb(results);
    },
    createFilter(qs) {
      return country => {
        return country.regionName.toLowerCase().indexOf(qs.toLowerCase()) !== -1;
      };
    },
    handleRegionSelect(region) {
      this.filterRegions = [region];
    },
    handleSelectAll(value) {
      this.$emit('select-all', value);
      this.handleSelectionChange(value);
    },
    handleSelect(selectList, value) {
      const isSelected = !!selectList.find(item => item.id === value.id);
      this.$emit('select', { ...value, isSelected });
      this.handleSelectionChange(selectList);
    },
    handleSelectionChange(value) {
      this.$emit('update-countries', value.map(item => item.id));
    },
    getSelectable(row) {
      return !row.disabled;
    },
  },
  components: {
    SelectionTable,
  },
};
</script>
<style lang="scss" scoped>
@import '@zoom/billing-responsive/styles/responsive.scss';

.countries-container {
  margin-top: 16px;
  width: 100%;
}

@include media(mobile,tablet){
  /deep/ .zm-table-column--selection{
    vertical-align: top !important;
  }
  /deep/ .zm-table__header thead tr th{
    background: #F7F9FA;
  }
  /deep/ .zm-row .zm-col:last-child{
    color: #131619;
  }
}

</style>
<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';
@import '@/styles/scss-utils.scss';

@include media(mobile,tablet){
  .zm-table__header thead tr th{
    @include darkBGColorTransparent;
  }
  .zm-row .zm-col:last-child{
    @include darkTextColor2;
  }
}
.selectable-country-list {
  .zm-autocomplete {
    width: 100%;
  }

  .zm-input__inner:focus {
    box-shadow: unset;
  }

  .zm-checkbox {
    line-height: 16px;
    padding: 0;
  }

  .zm-checkbox .zm-checkbox-wrap {
    min-height: 16px;
    line-height: 16px;
  }

  .zm-checkbox__inner {
    top: 4px;
  }
}

.is-keyboard-event {
  .selectable-country-list *:focus {
    box-shadow: 0 0 2px 2px #0e72ed inset;
  }
}

.zm-theme--dark {
  .search-container {
    // fix zm-tabs__content overflow hidden, hidden the focus box-shadow for zm-autocomplete input
    padding: 4px;
  }
}
</style>
